import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../config/api.config';

const getStoredCustomerId = () => {
  try {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (!storedUserInfo) {
      return null;
    }

    const parsedUserInfo = JSON.parse(storedUserInfo);
    return parsedUserInfo?.customer_id || null;
  } catch (error) {
    console.error('Failed to read stored customer ID from userInfo:', error);
    return null;
  }
};

/**
 * Get problems discovery data with filters and pagination
 * @param {string} clientId - Client identifier
 * @param {Object} params - Query parameters
 */
export const getProblemsDiscovery = async (clientId, params = {}) => {
  if (!clientId) {
    throw new Error('clientId is required for fetching problems discovery');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }


    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('clientId', clientId);
    
    // Add other parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Special handling for pagination and sorting parameters
        if (['page', 'pageSize', 'sortField', 'sortDirection'].includes(key)) {
          queryParams.append(key, value);
        } else {
          // All other parameters should be treated as filters
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(`filters[${key}]`, v));
          } else {
            queryParams.append(`filters[${key}]`, value);
          }
        }
      }
    });


    const response = await axios.get(
      `${API_BASE_URL}/problems/discovery?${queryParams.toString()}`,
      {
        headers: createAuthHeader(token)
      }
    );


    return response.data;
  } catch (error) {
    console.error('❌ [ProblemsAPI] Error fetching problems discovery:');
    console.error('   🔴 Error details:', error);
    if (error.response) {
      console.error('   📊 Response status:', error.response.status);
      console.error('   📝 Response data:', error.response.data);
    }
    throw error;
  }
};

/**
 * Get available filter values for problems discovery
 * @param {string} clientId - Client identifier
 * @param {Object} currentFilters - Current applied filters for faceted search
 */
export const getProblemsDiscoveryFilters = async (clientId, currentFilters = {}) => {
  if (!clientId) {
    throw new Error('clientId is required for fetching problems filters');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }


    // Build query parameters
    const params = new URLSearchParams();
    params.append('clientId', clientId);
    
    // Add filter parameters for faceted search
    Object.entries(currentFilters).forEach(([key, values]) => {
      if (values && Array.isArray(values) && values.length > 0) {
        values.forEach(value => {
          params.append(`filters[${key}]`, value);
        });
      }
    });

    const finalUrl = `${API_BASE_URL}/problems/discovery/filters?${params.toString()}`;

    const response = await axios.get(finalUrl, {
      headers: createAuthHeader(token)
    });

    return response.data;
  } catch (error) {
    console.error('❌ [ProblemsAPI] Error fetching problems filters:', error);
    throw error;
  }
};

/**
 * Get KPI metrics for problems discovery
 * @param {string} clientId - Client identifier
 */
export const getProblemsDiscoveryKPIs = async (clientId) => {
  if (!clientId) {
    throw new Error('clientId is required for fetching problems KPIs');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }


    const response = await axios.get(
      `${API_BASE_URL}/problems/discovery/kpis?clientId=${clientId}`,
      {
        headers: createAuthHeader(token)
      }
    );

    return response.data;
  } catch (error) {
    console.error('❌ [ProblemsAPI] Error fetching problems KPIs:', error);
    throw error;
  }
};

/**
 * Create a manual experiment for a problem
 * @param {string} clientId - Client identifier
 * @param {number} problemId - Problem identifier
 * @param {Object} experimentData - Experiment data
 */
export const createManualExperiment = async (clientId, problemId, experimentData) => {
  if (!clientId) {
    throw new Error('clientId is required for creating manual experiment');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.post(
      `${API_BASE_URL}/problems/${problemId}/experiments/manual?clientId=${clientId}`,
      experimentData,
      {
        headers: createAuthHeader(token)
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error creating manual experiment:', error);
    throw error;
  }
};

/**
 * Get AI recommendation for a problem
 * @param {string} clientId - Client identifier
 * @param {number} problemId - Problem identifier
 * @param {Object} options - Options for AI recommendation
 */
export const getAIRecommendation = async (clientId, problemId, options = {}) => {
  if (!clientId) {
    throw new Error('clientId is required for getting AI recommendation');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.post(
      `${API_BASE_URL}/problems/${problemId}/ai-recommendation?clientId=${clientId}`,
      { includeReasoning: true, ...options },
      {
        headers: createAuthHeader(token)
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting AI recommendation:', error);
    throw error;
  }
};

/**
 * Get aggregated problems discovery data with filters and pagination
 * @param {string} clientId - Client identifier
 * @param {Object} params - Query parameters including groupBy
 */
export const getProblemsDiscoveryAggregated = async (clientId, params = {}) => {
  if (!clientId) {
    throw new Error('clientId is required for fetching aggregated problems discovery');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('clientId', clientId);

    // Add groupBy parameter if provided
    if (params.groupBy !== undefined) {
      queryParams.append('groupBy', params.groupBy);
    }

    // Add other parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== 'groupBy') {
        // Special handling for pagination and sorting parameters
        if (['page', 'pageSize', 'sortField', 'sortDirection'].includes(key)) {
          queryParams.append(key, value);
        } else {
          // All other parameters should be treated as filters
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(`filters[${key}]`, v));
          } else {
            queryParams.append(`filters[${key}]`, value);
          }
        }
      }
    });

    const response = await axios.get(
      `${API_BASE_URL}/problems/discovery/aggregated?${queryParams.toString()}`,
      {
        headers: createAuthHeader(token)
      }
    );

    return response.data;
  } catch (error) {
    console.error('❌ [ProblemsAPI] Error fetching aggregated problems discovery:');
    console.error('   🔴 Error details:', error);
    if (error.response) {
      console.error('   📊 Response status:', error.response.status);
      console.error('   📝 Response data:', error.response.data);
    }
    throw error;
  }
};

/**
 * Get details for a group of experiments
 * @param {string} clientId - Client identifier
 * @param {number[]} experimentIds - Array of experiment IDs
 * @param {Object} options - Additional options for sorting
 */
export const getProblemsGroupDetails = async (clientId, experimentIds, options = {}) => {
  if (!clientId) {
    throw new Error('clientId is required for fetching problems group details');
  }

  if (!experimentIds || !Array.isArray(experimentIds) || experimentIds.length === 0) {
    throw new Error('experimentIds array is required for fetching problems group details');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('clientId', clientId);

    // Add experimentIds as array parameters
    experimentIds.forEach(id => queryParams.append('experimentIds', id));

    // Add sorting parameters if provided
    if (options.sortField) {
      queryParams.append('sortField', options.sortField);
    }
    if (options.sortDirection) {
      queryParams.append('sortDirection', options.sortDirection);
    }

    const response = await axios.get(
      `${API_BASE_URL}/problems/discovery/group-details?${queryParams.toString()}`,
      {
        headers: createAuthHeader(token)
      }
    );

    return response.data;
  } catch (error) {
    console.error('❌ [ProblemsAPI] Error fetching problems group details:', error);
    throw error;
  }
};

/**
 * Bulk accept multiple experiments
 * @param {string} clientId - Client identifier
 * @param {number[]} experimentIds - Array of experiment IDs to accept
 */
export const bulkAcceptExperiments = async (clientId, experimentIds, experimentsWithPrices = [], experimentProductUpdates = []) => {
  if (!clientId) {
    throw new Error('clientId is required for bulk accepting experiments');
  }

  if (!experimentIds || !Array.isArray(experimentIds) || experimentIds.length === 0) {
    throw new Error('experimentIds array is required for bulk accepting experiments');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.post(
      `${API_BASE_URL}/experiments/bulk/accept`,
      {
        clientId,
        experimentIds,
        experimentsWithPrices, // [{experimentId, originalPrice, isFallbackPrice}]
        experimentProductUpdates // [{experimentId, candidateProductId, discount}]
      },
      {
        headers: createAuthHeader(token)
      }
    );

    return response.data;
  } catch (error) {
    console.error('❌ [ProblemsAPI] Error bulk accepting experiments:', error);
    throw error;
  }
};

/**
 * Update experiment inline with partial updates
 * @param {string} clientId - Client identifier
 * @param {number} experimentId - Experiment ID
 * @param {Object} updates - Partial updates (dateFrom, dateTo, candidateProductId, discount, status)
 */
export const inlineUpdateExperiment = async (clientId, experimentId, updates) => {
  if (!clientId) {
    throw new Error('clientId is required for inline updating experiment');
  }

  if (!experimentId) {
    throw new Error('experimentId is required for inline updating experiment');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.patch(
      `${API_BASE_URL}/experiments/${experimentId}/inline-update?clientId=${clientId}`,
      updates,
      {
        headers: createAuthHeader(token)
      }
    );

    return response.data;
  } catch (error) {
    console.error('❌ [ProblemsAPI] Error inline updating experiment:', error);
    throw error;
  }
};

// For backward compatibility
export const problemsApi = {
  getProblemsDiscovery: (params) => {
    console.warn('problemsApi.getProblemsDiscovery is deprecated. Use direct import getProblemsDiscovery(clientId, params) instead.');
    const clientId = getStoredCustomerId();
    return getProblemsDiscovery(clientId, params);
  },
  getProblemsDiscoveryFilters: () => {
    console.warn('problemsApi.getProblemsDiscoveryFilters is deprecated. Use direct import getProblemsDiscoveryFilters(clientId, filters) instead.');
    const clientId = getStoredCustomerId();
    return getProblemsDiscoveryFilters(clientId);
  },
  getProblemsDiscoveryKPIs: () => {
    console.warn('problemsApi.getProblemsDiscoveryKPIs is deprecated. Use direct import getProblemsDiscoveryKPIs(clientId) instead.');
    const clientId = getStoredCustomerId();
    return getProblemsDiscoveryKPIs(clientId);
  },
  createManualExperiment: (problemId, experimentData) => {
    console.warn('problemsApi.createManualExperiment is deprecated. Use direct import createManualExperiment(clientId, problemId, experimentData) instead.');
    const clientId = getStoredCustomerId();
    return createManualExperiment(clientId, problemId, experimentData);
  },
  getAIRecommendation: (problemId, options) => {
    console.warn('problemsApi.getAIRecommendation is deprecated. Use direct import getAIRecommendation(clientId, problemId, options) instead.');
    const clientId = getStoredCustomerId();
    return getAIRecommendation(clientId, problemId, options);
  },
  getProblemsDiscoveryAggregated: (params) => {
    console.warn('problemsApi.getProblemsDiscoveryAggregated is deprecated. Use direct import getProblemsDiscoveryAggregated(clientId, params) instead.');
    const clientId = getStoredCustomerId();
    return getProblemsDiscoveryAggregated(clientId, params);
  },
  getProblemsGroupDetails: (experimentIds, options) => {
    console.warn('problemsApi.getProblemsGroupDetails is deprecated. Use direct import getProblemsGroupDetails(clientId, experimentIds, options) instead.');
    const clientId = getStoredCustomerId();
    return getProblemsGroupDetails(clientId, experimentIds, options);
  },
  bulkAcceptExperiments: (experimentIds) => {
    console.warn('problemsApi.bulkAcceptExperiments is deprecated. Use direct import bulkAcceptExperiments(clientId, experimentIds) instead.');
    const clientId = getStoredCustomerId();
    return bulkAcceptExperiments(clientId, experimentIds);
  },
  inlineUpdateExperiment: (experimentId, updates) => {
    console.warn('problemsApi.inlineUpdateExperiment is deprecated. Use direct import inlineUpdateExperiment(clientId, experimentId, updates) instead.');
    const clientId = getStoredCustomerId();
    return inlineUpdateExperiment(clientId, experimentId, updates);
  }
};

export default problemsApi;
